import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Shield, Mail, User, Globe, MessageSquare, CheckCircle, Award, Users, TrendingUp, AlertTriangle, Zap, Star, ArrowRight, Send, Phone, MapPin, Clock } from "lucide-react";
import DOMPurify from 'dompurify';
import Navbar from "@/components/Navbar";
import SEO from "@/components/SEO";

const ContactUs = () => {
  const [form, setForm] = useState({
    email: "",
    agree: false,
    message: "",
    subscribeNewsletter: false,
  });

  const [errors, setErrors] = useState<string[]>([]);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rateLimitExceeded, setRateLimitExceeded] = useState(false);
  const [isLoaded, setIsLoaded] = useState(true);
  const [csrfToken, setCsrfToken] = useState<string>("");
  
  const lastSubmissionTime = useRef<number>(0);
  const submissionCount = useRef<number>(0);


  // Fetch CSRF token on component mount
  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const response = await fetch("/api/csrf-token", {
          method: "GET",
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          setCsrfToken(data.csrf_token);
        }
      } catch (error) {
        console.error("Failed to fetch CSRF token:", error);
      }
    };
    fetchCsrfToken();
  }, []);

  const sanitizeInput = (input: string): string => {
    return DOMPurify.sanitize(input.trim(), { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" && "checked" in e.target ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const validate = () => {
    const errs: string[] = [];
    
    const cleanEmail = sanitizeInput(form.email);
    if (!cleanEmail) errs.push("Email is required.");
    else if (cleanEmail.length > 255) errs.push("Email must be under 255 characters.");
    else if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(cleanEmail)) errs.push("Email format is invalid.");
    
    const cleanMessage = sanitizeInput(form.message);
    if (!cleanMessage) errs.push("Message is required.");
    else if (cleanMessage.length > 2000) errs.push("Message must be under 2000 characters.");
    else if (cleanMessage.length < 10) errs.push("Message must be at least 10 characters.");
    
    if (!form.agree) errs.push("You must agree to the Privacy Policy.");
    
    return errs;
  };

  const checkRateLimit = (): boolean => {
    const now = Date.now();
    const timeSinceLastSubmission = now - lastSubmissionTime.current;
    
    if (timeSinceLastSubmission > 3600000) {
      submissionCount.current = 0;
    }
    
    if (submissionCount.current >= 3) {
      setRateLimitExceeded(true);
      return false;
    }
    
    if (timeSinceLastSubmission < 30000) {
      setErrors(["Please wait at least 30 seconds between submissions."]);
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);
    setSuccess(false);
    setRateLimitExceeded(false);

    if (!checkRateLimit()) {
      return;
    }

    const validationErrors = validate();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    
    lastSubmissionTime.current = Date.now();
    submissionCount.current += 1;
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: sanitizeInput(form.email),
          agree: form.agree,
          message: sanitizeInput(form.message),
          csrf_token: csrfToken,
        }),
      });
      if (res.status === 201) {
        setSuccess(true);
        
        // Handle newsletter subscription if user opted in
        if (form.subscribeNewsletter) {
          try {
            const newsletterRes = await fetch("/api/newsletter/subscribe", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({
                email: sanitizeInput(form.email),
                preferences: {
                  general: true,
                  offensive_security: false,
                  devsecops: false,
                  cloud_security: false
                },
                source: "contact_form"
              }),
            });
            
            if (newsletterRes.ok) {
              console.log("Newsletter subscription initiated");
            } else {
              console.warn("Newsletter subscription failed, but contact form succeeded");
            }
          } catch (err) {
            console.warn("Newsletter subscription error:", err);
          }
        }
        
        setForm({
          email: "",
          agree: false,
          message: "",
          subscribeNewsletter: false,
        });
      } else {
        let errorDetails = `HTTP ${res.status} ${res.statusText}`;
        try {
          const data = await res.json();
          if (data.errors) {
            errorDetails += `: ${data.errors.join(", ")}`;
          } else if (data.error) {
            errorDetails += `: ${data.error}`;
          }
        } catch (jsonErr) {
          // Could not parse JSON
        }
        setErrors([`Submission failed. ${errorDetails}`]);
      }
    } catch (err: unknown) {
      let errorMsg = "Network error. ";
      if (err instanceof TypeError && err.message.includes("Failed to fetch")) {
        errorMsg += "Could not connect to the server. This may be a CORS issue, server is down, or network connectivity problem.";
      } else if (err instanceof Error) {
        errorMsg += `${err.name}: ${err.message}`;
      } else {
        errorMsg += String(err);
      }
      console.error("Contact form submission error:", err);
      setErrors([errorMsg]);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes pulse {
            0%, 100% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.8;
              transform: scale(1.05);
            }
          }

          @keyframes float {
            0%, 100% {
              transform: translateY(0px) rotate(0deg);
            }
            33% {
              transform: translateY(-10px) rotate(-1deg);
            }
            66% {
              transform: translateY(-5px) rotate(1deg);
            }
          }

          .animate-fade-in {
            animation: fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }
          
          .content-loaded {
            opacity: 1;
            transform: translateY(0);
          }
          
          .content-loading {
            opacity: 0;
            transform: translateY(20px);
          }

          .gradient-text {
            background: linear-gradient(135deg, #00a86b 0%, #06b6d4 50%, #00a86b 100%);
            background-size: 200% 200%;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }

          .hover-glow {
            position: relative;
            overflow: hidden;
          }

          .hover-glow::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
            transition: left 0.6s;
          }

          .hover-glow:hover::before {
            left: 100%;
          }

          .glass-card {
            background: rgba(26, 26, 46, 0.6);
            backdrop-filter: blur(16px) saturate(180%);
            border: 1px solid rgba(255, 255, 255, 0.1);
            box-shadow: 
              0 8px 32px rgba(0, 0, 0, 0.2),
              inset 0 1px 0 rgba(255, 255, 255, 0.1);
          }
        `}
      </style>

      <div className="relative">
        <SEO 
          title="Contact CIPHER Security | Get Expert Cybersecurity Consultation"
          description="Contact CIPHER Security for professional cybersecurity consultation. Get expert penetration testing, DevSecOps, application security, and compliance solutions. Schedule your security assessment today."
          keywords={[
            'cybersecurity consultation',
            'contact cybersecurity company',
            'penetration testing quote',
            'DevSecOps consultation',
            'security assessment',
            'cybersecurity services contact',
            'security consultation',
            'vulnerability assessment contact',
            'enterprise security consultation',
            'cybersecurity expert contact'
          ]}
        />
        
        <Navbar />
        
        <div className="relative min-h-screen bg-[#0f0f17] overflow-hidden">
          {/* Background effects */}
          <div className="absolute inset-0 animate-fade-in">
            <div className="absolute inset-0 bg-gradient-to-b from-[#0f0f17] via-[#0a0a12] to-[#0f0f17]" />
            
            {/* Orbs */}
            <div 
              className="absolute top-32 -right-48 sm:right-16 w-[300px] sm:w-[400px] lg:w-[600px] h-[300px] sm:h-[400px] lg:h-[600px] rounded-full opacity-25"
              style={{
                background: 'radial-gradient(circle at 30% 30%, rgba(0, 168, 107, 0.12) 0%, rgba(6, 182, 212, 0.08) 50%, transparent 70%)',
                filter: 'blur(60px)',
                animation: 'pulse 8s ease-in-out infinite 1.5s'
              }}
            />
            <div 
              className="absolute bottom-32 -left-48 sm:left-16 w-[250px] sm:w-[350px] lg:w-[500px] h-[250px] sm:h-[350px] lg:h-[500px] rounded-full opacity-20"
              style={{
                background: 'radial-gradient(circle at 70% 70%, rgba(251, 191, 36, 0.10) 0%, rgba(251, 146, 60, 0.06) 50%, transparent 80%)',
                filter: 'blur(80px)',
                animation: 'pulse 10s ease-in-out infinite 2s'
              }}
            />
          </div>

          {/* Main Content */}
          <section className="relative z-10 flex items-center justify-center min-h-screen py-20 sm:py-24 lg:py-32">
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 w-full">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center w-full">
                
                {/* Left Side - Stats and Testimonial */}
                <div className="animate-fade-in order-2 lg:order-1">
                  <div className="mb-8">
                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
                      <span className="text-white">SEE </span>
                      <span
                        style={{
                          background: "linear-gradient(135deg, #00a86b 0%, #06b6d4 50%, #00a86b 100%)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          color: "#00a86b",
                          fontWeight: 700,
                        }}
                      >
                        CIPHER
                      </span>
                      <span className="text-white"> IN ACTION</span>
                    </h1>
                    <p className="text-base sm:text-lg text-gray-300 max-w-xl leading-relaxed">
                      See why CIPHER is the chosen security solution for developers and security teams alike — and what it can do for your team.
                    </p>
                    
                    {/* Contact Info */}
                    <div className="mt-6 flex flex-col sm:flex-row sm:gap-6 gap-2">
                      <div className="flex items-center gap-3 text-gray-300">
                        <Phone className="w-4 h-4 text-[#00a86b]" />
                        <span>+1 646-698-4144</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-300">
                        <Mail className="w-4 h-4 text-[#00a86b]" />
                        <span>sales@ciphersecurity.io</span>
                      </div>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4 sm:gap-6 mb-8">
                    <div>
                      <div className="text-2xl sm:text-4xl font-bold text-white mb-1">500+</div>
                      <div className="text-[10px] sm:text-sm font-semibold text-[#00a86b] uppercase tracking-wide">ASSESSMENTS</div>
                      <div className="text-xs text-gray-400 mt-1 hidden sm:block">Security evaluations completed across Fortune 500</div>
                    </div>
                    <div>
                      <div className="text-2xl sm:text-4xl font-bold text-white mb-1">85%</div>
                      <div className="text-[10px] sm:text-sm font-semibold text-[#06b6d4] uppercase tracking-wide">FASTER</div>
                      <div className="text-xs text-gray-400 mt-1 hidden sm:block">Vulnerability remediation with our DevSecOps approach</div>
                    </div>
                    <div>
                      <div className="text-2xl sm:text-4xl font-bold text-white mb-1">24-HR</div>
                      <div className="text-[10px] sm:text-sm font-semibold text-[#fbbf24] uppercase tracking-wide">RESPONSE</div>
                      <div className="text-xs text-gray-400 mt-1 hidden sm:block">Average time to identify and contain threats</div>
                    </div>
                  </div>

                  {/* Testimonial */}
                  <div className="glass-card p-4 sm:p-6 rounded-xl w-full lg:max-w-2xl border border-white/10"
                       style={{
                         background: 'rgba(26, 26, 46, 0.4)',
                         backdropFilter: 'blur(20px) saturate(180%)',
                         boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                       }}>
                    <div className="flex items-start mb-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#00a86b] to-[#06b6d4] flex items-center justify-center mr-4 flex-shrink-0">
                        <Shield className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-[#00a86b]">Enterprise Client</div>
                        <div className="text-xs text-gray-400">CISO, Fortune 500 Financial Services</div>
                      </div>
                    </div>
                    <blockquote className="text-gray-300 italic text-sm sm:text-base leading-relaxed mb-4">
                      "A key thing that CIPHER Security has done well is offer us a developer-centric product. We have an internal idea of what it means to be excellent at application security and CIPHER Security has been a partner that fits this vision delivering actionable risk mitigation guidance."
                    </blockquote>
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-gray-600 to-gray-700 flex items-center justify-center mr-3">
                        <Users className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="text-white font-semibold text-sm">Enterprise Client</div>
                        <div className="text-gray-400 text-xs">CISO, Fortune 500 Financial Services</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Side - Contact Form */}
<div className="animate-fade-in order-1 lg:order-2">
                  <div className="glass-card p-6 sm:p-8 lg:p-10 rounded-2xl w-full lg:max-w-lg border border-white/10" 
                       style={{
                         background: 'rgba(26, 26, 46, 0.4)',
                         backdropFilter: 'blur(20px) saturate(180%)',
                         boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                       }}>
                    <div className="mb-8">
                      <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3">Book a call</h2>
                    </div>
                    
                    <form onSubmit={handleSubmit} noValidate>
                      <div className="space-y-4 sm:space-y-6">
                        {rateLimitExceeded && (
                          <div className="bg-orange-900/60 border border-orange-500/40 text-orange-200 rounded-lg px-4 py-3 text-sm">
                            ⚠️ Too many submissions. Please try again in 1 hour.
                          </div>
                        )}
                        {errors.length > 0 && (
                          <div className="bg-red-900/60 border border-red-500/40 text-red-200 rounded-lg px-4 py-3 text-sm">
                            <ul className="list-disc pl-5">
                              {errors.map((err, i) => <li key={i}>{DOMPurify.sanitize(err)}</li>)}
                            </ul>
                          </div>
                        )}
                        {success && (
                          <div className="bg-green-900/60 border border-green-500/40 text-green-200 rounded-lg px-4 py-3 text-sm flex items-center">
                            <CheckCircle className="w-5 h-5 mr-2" />
                            Thank you! Your message has been sent successfully.
                          </div>
                        )}

                        <div>
                          <label className="block text-gray-300 text-sm sm:text-base font-medium mb-2 sm:mb-3" htmlFor="email">
                            Email
                          </label>
                          <input
                            className="w-full px-4 py-3 sm:py-4 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#06b6d4]/50 focus:border-[#06b6d4]/50 transition-all duration-200 text-sm sm:text-base border border-white/20"
                            style={{
                              background: 'rgba(15, 15, 23, 0.6)',
                              backdropFilter: 'blur(10px)',
                              boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.3)'
                            }}
                            type="email"
                            id="email"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            placeholder="Enter your email"
                            required
                            autoComplete="email"
                            maxLength={255}
                          />
                        </div>

                        <div>
                          <label className="block text-gray-300 text-sm sm:text-base font-medium mb-2 sm:mb-3" htmlFor="message">
                            Message
                          </label>
                          <textarea
                            className="w-full px-4 py-3 sm:py-4 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#06b6d4]/50 focus:border-[#06b6d4]/50 transition-all duration-200 text-sm sm:text-base border border-white/20 resize-none"
                            style={{
                              background: 'rgba(15, 15, 23, 0.6)',
                              backdropFilter: 'blur(10px)',
                              boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.3)'
                            }}
                            id="message"
                            name="message"
                            value={form.message}
                            onChange={handleChange}
                            placeholder="Tell us about your security needs..."
                            required
                            rows={4}
                            maxLength={2000}
                          />
                        </div>

                        <div className="text-xs text-gray-400 leading-relaxed py-2 px-3 sm:py-3 sm:px-4 rounded-lg border border-white/10"
                             style={{
                               background: 'rgba(255, 255, 255, 0.03)',
                               backdropFilter: 'blur(10px)'
                             }}>
                          In accordance with our <a href="/privacy-policy" className="underline text-[#06b6d4] hover:text-[#00a86b] transition-colors">Privacy Policy</a> you may be contacted about CIPHER or related products and services. You can unsubscribe at any time by visiting our{" "}
                          <a href="/preferences" className="underline text-[#06b6d4] hover:text-[#00a86b] transition-colors">Preference Center</a>.
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-start">
                            <input
                              type="checkbox"
                              id="agree"
                              name="agree"
                              checked={form.agree}
                              onChange={handleChange}
                              className="mr-2 sm:mr-3 mt-1 w-4 h-4 accent-[#00a86b] flex-shrink-0"
                              required
                            />
                            <label htmlFor="agree" className="text-xs text-gray-300">
                              I agree to the CIPHER <a href="/privacy-policy" className="underline text-[#06b6d4] hover:text-[#00a86b] transition-colors">Privacy Policy</a>
                            </label>
                          </div>
                          
                          <div className="flex items-start">
                            <input
                              type="checkbox"
                              id="subscribeNewsletter"
                              name="subscribeNewsletter"
                              checked={form.subscribeNewsletter}
                              onChange={handleChange}
                              className="mr-2 sm:mr-3 mt-1 w-4 h-4 accent-[#00a86b] flex-shrink-0"
                            />
                            <label htmlFor="subscribeNewsletter" className="text-xs text-gray-300">
                              📧 Subscribe to our weekly cybersecurity newsletter for threat intelligence, security insights, and industry updates
                            </label>
                          </div>
                        </div>

                        <button
                          type="submit"
                          className="w-full py-3 sm:py-4 rounded-lg bg-gradient-to-r from-[#00a86b] to-[#06b6d4] hover:from-[#00c77d] hover:to-[#0891b2] text-white font-semibold text-base sm:text-lg transition-all duration-300 transform hover:scale-[1.02] hover-glow disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center"
                          style={{
                            boxShadow: "0 4px 24px rgba(0, 168, 107, 0.25)"
                          }}
                          disabled={submitting || rateLimitExceeded}
                        >
                          {submitting ? (
                            <>
                              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Let's talk
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </section>

        </div>
      </div>
    </>
  );
};

export default ContactUs;